import { useContext, useEffect, useRef, useState } from 'react'
import Padded from '../../../components/Padded'
import Header from '../../../components/Header'
import Content from '../../../components/Content'
import FlexCol from '../../../components/FlexCol'
import { WalletContext } from '../../../providers/wallet'
import { WalletProvider, type LoanAsset, AddressType } from '@lendasat/lendasat-wallet-bridge'
import { collaborativeExit, getReceivingAddresses } from '../../../lib/asp'
import { isBTCAddress } from '../../../lib/address'
import { isValidArkAddress } from '@arkade-os/sdk'
import { isValidSendAmount, sendConfirmation } from '../../../lib/appRequest'
import { useBridgeConfirmation } from '../../../hooks/useBridgeConfirmation'
import BridgeConfirmSheet from '../../../components/BridgeConfirmSheet'

const IFRAME_URL = import.meta.env.VITE_SATORA_IFRAME_URL || 'https://app.satora.io'
const DEFAULT_SWAP_PATH = '/arkade:BTC/polygon:USDC'
const APP_NAME = 'Satora'

export default function AppSatora() {
  const { svcWallet } = useContext(WalletContext)
  const { approve, reject, request, requestConfirmation } = useBridgeConfirmation()
  const [arkAddress, setArkAddress] = useState<string | null>(null)

  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    const loadAddress = async () => {
      if (svcWallet) {
        try {
          const addresses = await getReceivingAddresses(svcWallet)
          setArkAddress(addresses.offchainAddr)
        } catch (error) {
          console.error('Failed to load Arkade address:', error)
        }
      }
    }
    loadAddress()
  }, [svcWallet])

  useEffect(() => {
    if (!iframeRef.current) return

    const provider = new WalletProvider(
      {
        capabilities: () => {
          return {
            bitcoin: {
              signPsbt: false,
              sendBitcoin: false,
            },
            loanAssets: {
              supportedAssets: [],
              canReceive: false,
              canSend: false,
            },
            nostr: {
              hasNpub: false,
            },
            ark: {
              canSend: true,
              canReceive: true,
            },
          }
        },
        async onGetAddress(addressType: AddressType): Promise<string> {
          switch (addressType) {
            case AddressType.BITCOIN:
            case AddressType.LOAN_ASSET:
              throw Error('Address type not supported')
            case AddressType.ARK:
              if (!arkAddress) throw new Error('Arkade address not yet loaded')
              return arkAddress
          }
        },
        async onSendToAddress(address: string, amount: number, asset: 'bitcoin' | LoanAsset): Promise<string> {
          if (!svcWallet) {
            throw Error('Wallet not initialized')
          }

          if (!isValidSendAmount(amount)) {
            throw new Error('Invalid amount')
          }

          switch (asset) {
            case 'bitcoin': {
              const offchain = isValidArkAddress(address)
              if (!offchain && !isBTCAddress(address)) throw Error(`Unsupported address ${address}`)

              const approved = await requestConfirmation(sendConfirmation(APP_NAME, address, amount, offchain))
              if (!approved) throw new Error('Payment declined')

              if (!offchain) return await collaborativeExit(svcWallet, amount, address)

              const txId = await svcWallet.send({ amount, address })
              if (!txId) throw new Error('Unable to send bitcoin')
              return txId
            }
            case 'UsdcPol':
            case 'UsdtPol':
            case 'UsdcEth':
            case 'UsdtEth':
            case 'UsdcStrk':
            case 'UsdtStrk':
            case 'UsdcSol':
            case 'UsdtSol':
            case 'UsdtLiquid':
              throw new Error('Not implemented for Satora')
            case 'Usd':
            case 'Eur':
            case 'Chf':
            case 'Mxn':
              throw new Error('Not implemented for Satora')
          }
        },
      },
      [IFRAME_URL],
    )

    provider.listen(iframeRef.current)

    return () => {
      provider.destroy()
    }
  }, [svcWallet, arkAddress, requestConfirmation])

  return (
    <>
      <Header text='Satora' back />
      <Content>
        <Padded>
          <FlexCol gap='2rem' between>
            <iframe
              ref={iframeRef}
              src={`${IFRAME_URL}${DEFAULT_SWAP_PATH}`}
              title='Satora'
              className='satora-iframe'
              allow='clipboard-write; clipboard-read'
              style={{ height: '100%' }}
            />
          </FlexCol>
        </Padded>
      </Content>
      <BridgeConfirmSheet request={request} onApprove={approve} onReject={reject} />
    </>
  )
}
